/**
 * URL validation and normalization utilities for secure iframe rendering
 */

// Trusted domains whitelist - add your trusted domains here
const TRUSTED_DOMAINS = [
  'knack.com',
  'knackcdn.com',
  'amazonaws.com',
  'cloudfront.net'
  // Add other trusted domains as needed
] as const;

/**
 * Validates if a URL is safe for iframe rendering
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
export function isValidIframeUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);

    // Only allow HTTPS protocol for security
    if (urlObj.protocol !== 'https:') {
      return false;
    }

    // Check if hostname is in trusted domains
    const hostname = urlObj.hostname.toLowerCase();

    // Allow exact matches or subdomains of trusted domains
    const isTrustedDomain = TRUSTED_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    );

    return isTrustedDomain;
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Normalizes and validates a URL for secure iframe rendering
 * @param url - The URL to normalize
 * @returns The normalized URL if valid, null if invalid
 */
export function normalizeIframeUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);

    // Ensure HTTPS protocol
    urlObj.protocol = 'https:';

    const normalizedUrl = urlObj.toString();

    if (isValidIframeUrl(normalizedUrl)) {
      return normalizedUrl;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Gets safe iframe sandbox attributes
 * @returns Sandbox attribute string with security restrictions
 */
export function getIframeSandboxAttributes(): string {
  return 'allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation';
}
